import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { useEffect, useRef } from 'react';
import { getJitteredInterval } from 'utils/pollingUtility';
import {toast} from 'react-toastify';

export const useReleasingPriorityList = () => {
  const { ReleasingPriorityStore } = useStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  useEffect(() => {
    ReleasingPriorityStore.fetchReleasingPriorityData();
  }, []);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await ReleasingPriorityStore.pollReleasingPriorityData();
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Releasing Priority List data!');
    }
  }, POLLING_INTERVAL);

  // Helper function to convert 24-hour format to 12-hour format
  const convertTo12HourFormat = (hours: number): number => {
    if (hours === 0) {
      return 12;
    }
    
    if (hours > 12) {
      return hours - 12;
    }
    
    return hours;
  };

  const getDateWithTime = () => {
    const date = new Date();
    const month = date.getMonth() + 1; // getMonth is 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Convert to 12-hour format
    const hour12 = convertTo12HourFormat(hours);
    const ampm = hours >= 12 ? 'PM' : 'AM';

    const formattedDate = `${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}-${year}`;
    const formattedTime = `${hour12 < 10 ? '0' + hour12 : hour12}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds} ${ampm}`;

    return `${formattedDate} ${formattedTime}`;
  };

  const handlePrint = () => {
    const printContents = document.getElementById('content');

    if (!printContents) {
      alert('No element with id="content" found!');
      return;
    }

    // Collect all class names used in the print div
    const classSet = new Set<string>();
    printContents.querySelectorAll('*').forEach(el => {
      if (typeof el.className === 'string') {
        el.className.split(' ').forEach(cls => classSet.add(cls));
      }
    });
    if (typeof printContents.className === 'string') {
      printContents.className.split(' ').forEach(cls => classSet.add(cls));
    }

    // Extract CSS rules for those class names
    let extractedCSS = '';
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from((sheet).cssRules || [])) {
          if ((rule as CSSStyleRule).selectorText !== undefined) {
            const selector = (rule as CSSStyleRule).selectorText;
            if (
              selector &&
              Array.from(classSet).some(cls =>
                selector.split(',').some(sel => sel.includes(`.${cls}`))
              )
            ) {
              extractedCSS += rule.cssText + '\n';
            }
          }
        }
      } catch {
        // Some stylesheets may be cross-origin and not accessible
        continue;
      }
    }

    // Copy <base> tag for relative URLs (images, fonts, etc.)
    const base = document.querySelector('base');
    const baseTag = base ? base.outerHTML : '';

    // Copy all styles and link tags
    let fontLinks = '';
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(node => {
      fontLinks += node.outerHTML;
    });

    // Open print window and inject content and styles
    const clone = printContents.cloneNode(true) as HTMLElement;

    // Update the date display in the cloned content to include timestamp
    const dateElement = clone.querySelector('.dateContainer p');
    if (dateElement) {
      dateElement.innerHTML = `<span>Date: </span>${getDateWithTime()}`;
    }

    const printWindow = window.open('', '', 'width=900,height=650');
    if (!printWindow) {
      alert('Could not open print window!');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          ${baseTag}
          <title>Releasing Priority List</title>
          ${fontLinks}
          <style>
            @page { size: auto; margin: 10mm; }
            body { 
              background: white !important; 
            }
            #print-btn { display: none !important; }
            #notes-tooltip { display: none !important; }
            ${extractedCSS}
          </style>
        </head>
        <body>
          ${clone.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    printWindow.onload = function () {
      setTimeout(function () {
        printWindow.print();
        printWindow.close();
      }, 700);
    };
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await ReleasingPriorityStore.refreshData();
  };

  return {
    isLoading: ReleasingPriorityStore.isLoading,
    releasingPriorityData: ReleasingPriorityStore.releasingPriorityData,
    contentRef,
    getDateWithTime,
    handlePrint,
    refreshData
  };
};
